import React from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { isValidAggregationRange } from "../../../utils/aggregationUtils";

export type DatePreset = "7d" | "1m" | "6m" | "1y";

interface ParticipantCoverageFiltersProps {
  disabled: boolean;
  fromDate: string;
  endDate: string;
  preset: DatePreset | null;
  onFromDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
  onPresetChange: (preset: DatePreset | null) => void;
  onRun: () => void;
  running: boolean;
  showAggregation?: boolean;
  aggregationType?: string;
  onAggregationTypeChange?: (value: string) => void;
  selectedMetric?: string | null;
  requireMetricSelection?: boolean;
}

const ParticipantCoverageFilters: React.FC<ParticipantCoverageFiltersProps> = ({
  disabled,
  fromDate,
  endDate,
  preset,
  onFromDateChange,
  onEndDateChange,
  onPresetChange,
  onRun,
  running,
  showAggregation = false,
  aggregationType = "none",
  onAggregationTypeChange,
  selectedMetric = null,
  requireMetricSelection = false,
}) => {
  const selectorWidth = { xs: "100%", md: 220 };

  return (
    <Stack spacing={2}>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <ToggleButtonGroup
          value={preset}
          exclusive
          onChange={(_, nextPreset) => onPresetChange(nextPreset)}
          size="small"
          disabled={disabled || running}
          sx={{
            "& .MuiToggleButton-root": {
              borderColor: "#8C1515",
              color: "#8C1515",
              "&.Mui-selected": {
                backgroundColor: "#8C1515",
                color: "#FFFFFF",
                "&:hover": {
                  backgroundColor: "#B33A3A",
                },
              },
            },
          }}
        >
          <ToggleButton value="7d">Last 7 Days</ToggleButton>
          <ToggleButton value="1m">Last 1 Month</ToggleButton>
          <ToggleButton value="6m">Last 6 Months</ToggleButton>
          <ToggleButton value="1y">Last 1 Year</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems="flex-end"
      >
        <TextField
          label="From Date"
          type="date"
          value={fromDate}
          onChange={(e) => onFromDateChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: selectorWidth }}
          disabled={disabled || running}
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => onEndDateChange(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: selectorWidth }}
          disabled={disabled || running}
        />

        {showAggregation && (
          <FormControl
            size="small"
            disabled={disabled || running}
            sx={{ width: selectorWidth }}
          >
            <InputLabel>Aggregations</InputLabel>
            <Select
              value={aggregationType}
              label="Aggregations"
              onChange={(e) => onAggregationTypeChange?.(e.target.value)}
            >
              <MenuItem value="none">None</MenuItem>
              <MenuItem
                value="weekly"
                disabled={!isValidAggregationRange(fromDate, endDate, "weekly")}
              >
                Weekly
              </MenuItem>
              <MenuItem
                value="monthly"
                disabled={
                  !isValidAggregationRange(fromDate, endDate, "monthly")
                }
              >
                Monthly
              </MenuItem>
              <MenuItem
                value="yearly"
                disabled={!isValidAggregationRange(fromDate, endDate, "yearly")}
              >
                Yearly
              </MenuItem>
            </Select>
          </FormControl>
        )}

        <Button
          variant="contained"
          onClick={onRun}
          disabled={
            disabled ||
            running ||
            !fromDate ||
            !endDate ||
            (requireMetricSelection && !selectedMetric)
          }
          sx={{
            minWidth: 140,
            flexShrink: 0,
            backgroundColor: "#8C1515",
            "&:hover": { backgroundColor: "#B33A3A" },
          }}
        >
          {running ? "Running..." : "Run"}
        </Button>
      </Stack>
    </Stack>
  );
};

export default ParticipantCoverageFilters;
